import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';

export interface User {
    id: string;
    name: string;
    email: string;
    role: 'administrador' | 'medico' | 'asistente' | 'usuario'; 
    institutionId?: string;
    lastname?: string;
    cedula?: string;
    username?: string;
    active?: boolean;
}

interface AuthContextType {
    user: User | null;
    login: (email: string, password: string) => Promise<boolean>;
    logout: () => void;
    isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

const isValidUser = (obj: any): obj is User => {
    return obj && typeof obj === 'object' && 'id' in obj && 'email' in obj && 'role' in obj;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // 1. CARGAR SESIÓN AL INICIAR (CORREGIDO A LOCALSTORAGE)
    useEffect(() => {
        const loadStoredAuth = () => {
            try {
                // ✅ CORREGIDO: Usar localStorage
                const storedUser = localStorage.getItem('medical_app_user');
                const storedToken = localStorage.getItem('token');
                
                if (storedUser && storedToken) {
                    const parsedUser = JSON.parse(storedUser);
                    if (isValidUser(parsedUser)) {
                        setUser(parsedUser);
                    } else {
                        throw new Error('Datos de usuario inválidos');
                    }
                }
            } catch (error) {
                console.error('Error al cargar sesión persistente:', error);
                // ✅ CORREGIDO: Limpieza en localStorage
                localStorage.removeItem('medical_app_user');
                localStorage.removeItem('token');
            } finally {
                setIsLoading(false);
            }
        };

        loadStoredAuth();
    }, []);

    // 2. FUNCIÓN DE LOGOUT (CORREGIDO A LOCALSTORAGE)
    const logout = useCallback(() => {
        setUser(null);
        // ✅ CORREGIDO: Borrar de localStorage
        localStorage.removeItem('medical_app_user');
        localStorage.removeItem('token');
        console.log('👋 Sesión cerrada correctamente');
    }, []);

    // 3. TEMPORIZADOR DE INACTIVIDAD (30 MINUTOS)
    useEffect(() => {
        if (!user) return;

        const INACTIVITY_LIMIT = 30 * 60 * 1000; 
        let timeoutId: NodeJS.Timeout;

        const resetTimer = () => {
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                console.log("⏳ Sesión expirada por inactividad (30 min)");
                logout();
                alert("Su sesión ha cerrado por inactividad.");
            }, INACTIVITY_LIMIT);
        };

        window.addEventListener('mousemove', resetTimer);
        window.addEventListener('keypress', resetTimer);
        window.addEventListener('click', resetTimer);
        window.addEventListener('scroll', resetTimer);

        resetTimer();

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            window.removeEventListener('mousemove', resetTimer);
            window.removeEventListener('keypress', resetTimer);
            window.removeEventListener('click', resetTimer);
            window.removeEventListener('scroll', resetTimer);
        };
    }, [user, logout]);

    // 4. FUNCIÓN DE LOGIN (CORREGIDO A LOCALSTORAGE)
    const login = async (email: string, password: string): Promise<boolean> => {
        try {
            const response = await fetch(`${API_URL}/api/login`, { 
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }) 
            });

            console.log('Status del servidor:', response.status);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('❌ Error en respuesta:', response.status, errorData);
                return false;
            }

            const data = await response.json();
            console.log('Respuesta completa del servidor:', data);
            
            if (data.success && data.user && data.token) {
                console.log('✅ Acceso concedido para:', data.user.email);
                
                // ✅ CORREGIDO: Guardar en localStorage
                localStorage.setItem('token', data.token);
                localStorage.setItem('medical_app_user', JSON.stringify(data.user));
                
                setUser(data.user);
                return true;
            } else {
                console.warn('❌ El servidor respondió success:false o faltan datos del usuario');
                return false;
            }
            
        } catch (error) {
            console.error('❌ Error crítico de conexión:', error);
            return false;
        }
    };

    return (
        <AuthContext.Provider value={{ user, login, logout, isLoading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth debe ser usado dentro de un AuthProvider');
    }
    return context;
};